import { isCloudReportVariant, type CloudReportFile } from '../../supabase/functions/_shared/reportsProtocol';
import type { V2ReportRecord } from '../domain/report';
import { currentAccessToken } from '../backend/aiClient';
import { getReportsTransport } from '../backend/reportsClient';
import { scopedStorageKey } from '../persistence/accountScope';
import { V2_REPORT_UPLOADS_KEY } from '../persistence/persistenceContract';
import { getReportBytes } from '../persistence/v2Database';
import { createStore } from './createStore';

// THE CLOUD COPIES OF DELIVERED PDFS (Owner decision list 2026-10-19 items 25 / 27 / 28): every PDF a final
// report run produces (report, Chinese copy / summary, signed agreement, addenda) is copied to the
// inspector's account — Cloudflare R2 through reports-v2 — so it stays readable from another device, the web
// version or after a reinstall, read-only. The device keeps its own copy; the upload is queued per account
// and retried (after generation, on every sign-in, when the app comes back to the front) until the server
// confirms. Opening a cloud copy asks for a link that works for five minutes.
export interface PendingUpload {
  reportId: string;
  inspectionId: string;
  variant: string;
  fileName: string;
  pageCount: number;
  generatedAt: number;
  address: string;
}

export type CloudState = 'unknown' | 'ok' | 'offline' | 'notConfigured' | 'unavailable';

export interface ReportCloudState {
  files: CloudReportFile[];
  pending: string[];
  state: CloudState;
  busy: boolean;
}

export const reportCloudStore = createStore<ReportCloudState>({ files: [], pending: [], state: 'unknown', busy: false });

function readQueue(): PendingUpload[] {
  try {
    const key = scopedStorageKey(V2_REPORT_UPLOADS_KEY);
    if (key === null) return [];
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? '[]') as unknown;
    return Array.isArray(parsed) ? parsed.filter((entry): entry is PendingUpload => typeof entry === 'object' && entry !== null && typeof (entry as PendingUpload).reportId === 'string') : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: readonly PendingUpload[]): void {
  try {
    const key = scopedStorageKey(V2_REPORT_UPLOADS_KEY);
    if (key !== null) {
      if (queue.length === 0) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, JSON.stringify(queue));
    }
  } catch {
    // storage refusal must never block field work; the PDFs stay on the device
  }
  reportCloudStore.set((prev) => ({ ...prev, pending: queue.map((entry) => entry.reportId) }));
}

export function queueReportUploads(records: readonly V2ReportRecord[], address: string): void {
  const queue = readQueue();
  for (const record of records) {
    if (queue.some((entry) => entry.reportId === record.id)) continue;
    queue.push({ reportId: record.id, inspectionId: record.inspectionId, variant: record.variant, fileName: record.fileName, pageCount: record.pageCount, generatedAt: record.generatedAt, address });
  }
  writeQueue(queue);
}

function stateFor(code: string): CloudState {
  if (code === 'offline' || code === 'network' || code === 'timeout') return 'offline';
  if (code === 'notConfigured') return 'notConfigured';
  return 'unavailable';
}

let flushing: Promise<void> | null = null;

// Uploads what is queued, oldest first; stops at the first failure (it is retried later).
export async function flushReportUploads(): Promise<void> {
  if (flushing) return flushing;
  flushing = (async () => {
    reportCloudStore.set((prev) => ({ ...prev, pending: readQueue().map((entry) => entry.reportId) }));
    for (const entry of readQueue()) {
      if (!isCloudReportVariant(entry.variant)) {
        writeQueue(readQueue().filter((item) => item.reportId !== entry.reportId));
        continue;
      }
      const blob = await getReportBytes(entry.reportId);
      // The PDF is no longer on this device (deleted by the inspector): nothing to copy.
      if (!blob) {
        writeQueue(readQueue().filter((item) => item.reportId !== entry.reportId));
        continue;
      }
      const token = await currentAccessToken();
      const outcome = await getReportsTransport().upload(token, { reportId: entry.reportId, inspectionId: entry.inspectionId, variant: entry.variant, fileName: entry.fileName, pageCount: entry.pageCount, generatedAt: entry.generatedAt, address: entry.address }, await blob.arrayBuffer());
      if (outcome.kind !== 'ok') {
        reportCloudStore.set((prev) => ({ ...prev, state: stateFor(outcome.code) }));
        // A file the server refuses (not a PDF, too large) is not retried forever.
        if (outcome.code === 'rejected') writeQueue(readQueue().filter((item) => item.reportId !== entry.reportId));
        return;
      }
      writeQueue(readQueue().filter((item) => item.reportId !== entry.reportId));
      if (outcome.response.action === 'upload') {
        const file = outcome.response.file;
        reportCloudStore.set((prev) => ({ ...prev, state: 'ok', files: [file, ...prev.files.filter((f) => f.reportId !== file.reportId)] }));
      }
    }
  })();
  try {
    await flushing;
  } finally {
    flushing = null;
  }
}

export async function refreshCloudReports(): Promise<void> {
  reportCloudStore.set((prev) => ({ ...prev, busy: true }));
  try {
    const token = await currentAccessToken();
    const outcome = await getReportsTransport().list(token);
    if (outcome.kind === 'ok' && outcome.response.action === 'list') {
      const files = outcome.response.files;
      reportCloudStore.set((prev) => ({ ...prev, files, state: 'ok' }));
    } else if (outcome.kind === 'failed') reportCloudStore.set((prev) => ({ ...prev, state: stateFor(outcome.code) }));
  } finally {
    // An answer that never came still ends the "reading" state the Reports screen shows (issue 15).
    reportCloudStore.set((prev) => ({ ...prev, busy: false, state: prev.state === 'unknown' ? 'unavailable' : prev.state }));
  }
}

// "先验身份再发临时链接": the server checks the file is this account's and answers a five-minute link.
export async function cloudReportLink(fileId: string): Promise<string | null> {
  const token = await currentAccessToken();
  const outcome = await getReportsTransport().link(token, fileId);
  return outcome.kind === 'ok' && outcome.response.action === 'link' ? outcome.response.url : null;
}

export function resetReportCloudStore(): void {
  flushing = null;
  reportCloudStore.set(() => ({ files: [], pending: [], state: 'unknown', busy: false }));
}
